import type { EventItem } from '@/types';

/**
 * Represents the structure of a category received from the backend API.
 */
export interface EventItemDTO {
  id: number;
  element_type: string;
  sort_order: number;
  categories: string[];
  special_categories: string[];
  venue: number;
  language: string;
  languages: string[];
  localizations: {
    [key: string]: {
      description?: string;
      info1?: string;
      info2?: string;
      remarks?: string;
      button_text_find_tickets?: string;
      button_text_buy_tickets?: string;
      ticket_info_message_text?: string;
      price_override_text?: string;
      single_sign_on_price_override_text?: string;
    };
  };
  photo: string;
  info1: string;
  info2: string;
  eventtime: number;
  salestart: number;
  saleend: number;
  eventtime_gmt_offset: number;
  salestart_gmt_offset: number;
  saleend_gmt_offset: number;
  tz_name: string;
  promoted: boolean;
  aggregated_categories_event_order: number;
  aggregated_special_categories_event_order: boolean;
  soldout: boolean;
  few_tickets: boolean;
  doorsale_button: boolean;
  doorsale_soldout: boolean;
  eventtime_override: string | null;
  price: number;
  status_message_text: string;
  status_message_visible: boolean;
  price_override_active: boolean;
  price_override_text: string;
  single_sign_on_price_override_active: boolean;
  single_sign_on_price_override_text: string;
  external_ticketsale_price: number;
  external_ticketsale_url: string;
  external_ticketsale_soldout: boolean;
  hidden_on_frontpage: boolean;
  is_attached_to_private_category: boolean;
  frontpage_event_highlight_in_calendar: boolean;
  frontpage_event_text_desktop_event_name: string;
  frontpage_event_text_desktop_date: string;
  frontpage_event_text_desktop_venue: string;
  frontpage_event_text_desktop_categories: string;
  frontpage_event_text_desktop_few_tickets: string;
  frontpage_event_text_desktop_soldout: string;
  frontpage_event_text_desktop_price: string;
  frontpage_event_text_mobile_event_name: string;
  frontpage_event_text_mobile_date: string;
  frontpage_event_text_mobile_venue: string;
  frontpage_event_text_mobile_categories: string;
  frontpage_event_text_mobile_few_tickets: string;
  frontpage_event_text_mobile_soldout: string;
  frontpage_event_text_mobile_price: string;
  tickets: [];
  sso_prices: {
    [key: string]: {
      price: number | null;
      dankort_price: number | null;
    };
  };
  remaining_tickets: number; // TODO delete from here: eventdetail / dynamic json stuff
}

/**
 * Transforms a CategoryDTO object into a Category object used in the frontend.
 * @param dto - The CategoryDTO object received from the backend.
 * @returns The transformed EventItem object.
 */
export function mapDtoToEventItem(dto: EventItemDTO): EventItem {
  return {
    ...dto,
    categories: dto.categories.map(Number),
    special_categories: dto.special_categories.map(Number),
    frontpage_event_text_desktop_event_name: Boolean(dto.frontpage_event_text_desktop_event_name),
    frontpage_event_text_desktop_date: Boolean(dto.frontpage_event_text_desktop_date),
    frontpage_event_text_desktop_venue: Boolean(dto.frontpage_event_text_desktop_venue),
    frontpage_event_text_desktop_categories: Boolean(dto.frontpage_event_text_desktop_categories),
    frontpage_event_text_desktop_few_tickets: Boolean(dto.frontpage_event_text_desktop_few_tickets),
    frontpage_event_text_desktop_soldout: Boolean(dto.frontpage_event_text_desktop_soldout),
    frontpage_event_text_mobile_event_name: Boolean(dto.frontpage_event_text_mobile_event_name),
    frontpage_event_text_mobile_date: Boolean(dto.frontpage_event_text_mobile_date),
    frontpage_event_text_mobile_venue: Boolean(dto.frontpage_event_text_mobile_venue),
    frontpage_event_text_mobile_categories: Boolean(dto.frontpage_event_text_mobile_categories),
    frontpage_event_text_mobile_few_tickets: Boolean(dto.frontpage_event_text_mobile_few_tickets),
    frontpage_event_text_mobile_soldout: Boolean(dto.frontpage_event_text_mobile_soldout),
    frontpage_event_text_desktop_price: Boolean(dto.frontpage_event_text_desktop_price),
    frontpage_event_text_mobile_price: Boolean(dto.frontpage_event_text_mobile_price),
  };
}

/**
 * Transforms an array of EventItemDTO objects into an array of EventItem objects.
 * @param dtos - The array of EventItemDTO objects received from the backend.
 * @returns The transformed array of EventItem objects.
 */
export function mapDtosToEventItems(dtos: EventItemDTO[]): EventItem[] {
  return dtos.map(mapDtoToEventItem);
}